import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, money } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { BundleCard } from "../components/BundleCard";
import {
  ArrowRight, ShieldCheck, FlaskConical, Truck, Lock, MapPin, Microscope, Beaker,
  Droplet, ScrollText, PackageCheck, ChevronDown, Activity, Brain, HeartPulse, Sparkle,
} from "lucide-react";

const RETA_FEATURE_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/4c7wtkc9_Ultra_close-up_specific_detail_2K_202607191628.webp";
const ATHLETE_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/82jyhb1n_Athlete.webp";
const ATHLETE_GIRL_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/3c3l0aij_Athlete_in_blue_lighting_2K_202607191703.webp";
const MOTSC_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_labs-ecommerce-demo/artifacts/lsyb0fbd_Replace_product_with_vial_underw..._202607200159.webp";

// HERO VIDEO: paste your Higgsfield/AI hero video URL here (mp4). Leave empty to use the composite hero image below.
const HERO_VIDEO = "";
// Hyperrealistic 3D composite of the full vial lineup (generated). Swap HERO_IMG to HERO_IMG_ALT for the splash version.
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c63dc54c-84c8-4f10-bcbd-d8c0b2d53297/images/e2422c2f1844a178879bcc026bac3a325ade6a5f771c4607a64958d83c650c18.jpeg";
const HERO_IMG_ALT = "https://static.prod-images.emergentagent.com/jobs/c63dc54c-84c8-4f10-bcbd-d8c0b2d53297/images/1e7352a580a89d1a517b825ed2e7c9214ba3da70a45691b7e8858051fcc8a41b.jpeg";
const HERO_POSTER = HERO_IMG;

const CAT_ORDER = ["Analog Peptides", "Peptide Fragments", "Cofactors & Conjugates", "Blends", "Neuropeptides", "Research Supplies"];

const STATS = [
  { value: 99, suffix: "%", label: "Purity verified by HPLC" },
  { value: 7, suffix: "", label: "Point testing standard" },
  { value: 100, suffix: "%", label: "US based fulfillment" },
  { value: 24, suffix: "h", label: "Typical dispatch window" },
];

const TIB = [
  { icon: ScrollText, t: "COA Certified", s: "Every batch tested" },
  { icon: FlaskConical, t: "99%+ Purity", s: "HPLC and mass spec" },
  { icon: Truck, t: "Fast Dispatch", s: "Business day shipping" },
  { icon: Lock, t: "Secure Payment", s: "Encrypted checkout" },
  { icon: MapPin, t: "US Based", s: "Domestic fulfillment" },
];

const BEYOND = [
  { n: "01", title: "Endotoxins", misses: "HPLC and mass spectrometry measure the molecule, not the biological residue around it.", matters: "Bacterial endotoxins stay active on their own. They trigger immune responses that corrupt results before an experiment begins. We hold every batch to a conservative limit." },
  { n: "02", title: "Sterility", misses: "A purity test never cultures for living contamination. A vial can pass purity and still grow bacteria or fungi.", matters: "Live contamination introduces variables you cannot account for. We verify every batch against sterility standards." },
  { n: "03", title: "Heavy metals", misses: "Lead, arsenic, mercury, and cadmium are inorganic. Chromatography is blind to them entirely.", matters: "Trace metals accumulate and skew long studies. We screen every batch with a separate method built to detect them." },
  { n: "04", title: "Batch consistency", misses: "Testing one vial once says nothing about the rest of the production run.", matters: "Reproducibility is the foundation of legitimate research. We test to confirm vial nine hundred matches vial one." },
];

const PATHWAYS = [
  { n: "01", icon: Activity, title: "Growth hormone", body: "Secretagogues studied across endocrine and somatotropic pathways.", to: "/?cat=Analog%20Peptides" },
  { n: "02", icon: HeartPulse, title: "Metabolic", body: "Peptides investigated in glucose regulation and metabolic function.", to: "/?cat=Analog%20Peptides" },
  { n: "03", icon: Beaker, title: "Tissue repair", body: "Sequences researched in cellular regeneration and recovery.", to: "/?cat=Peptide%20Fragments" },
  { n: "04", icon: Sparkle, title: "Longevity", body: "Cofactors explored in cellular aging and senescence.", to: "/?cat=Cofactors%20%26%20Conjugates" },
  { n: "05", icon: Brain, title: "Neurological", body: "Peptides studied in cognitive and neural pathways.", to: "/?cat=Neuropeptides" },
];

const COMMITMENTS = [
  { n: "01", stat: "Every batch", title: "Independent testing", body: "Each lot is verified by an independent laboratory before it is offered for sale." },
  { n: "02", stat: "Business day", title: "Fast dispatch", body: "Orders placed on a business day leave our US facility quickly, tracked and insured." },
  { n: "03", stat: "72 hours", title: "Damage coverage", body: "Broken in transit or the wrong item shipped. Tell us within 72 hours and we replace it at no cost." },
  { n: "04", stat: "100% US", title: "Domestic fulfillment", body: "Packed and shipped inside the United States from our own facility." },
  { n: "05", stat: "Zero marks", title: "Discreet packaging", body: "Plain outer packaging with a generic return address. Nothing on the box identifies the contents." },
  { n: "06", stat: "Under 6 hrs", title: "Real support", body: "Researchers on staff answer sourcing, reconstitution, and certificate questions directly." },
];

const VERIFIED = [
  { icon: Microscope, label: "Independent laboratory" },
  { icon: FlaskConical, label: "HPLC and mass spec" },
  { icon: ShieldCheck, label: "Endotoxin screened" },
  { icon: MapPin, label: "US fulfillment" },
  { icon: Lock, label: "Encrypted checkout" },
];

const TESTING = [
  { n: "01", title: "HPLC purity", body: "Reverse phase chromatography quantifies purity, the headline number on every certificate." },
  { n: "02", title: "Mass spec identity", body: "Mass to charge analysis confirms the compound is exactly what the label claims." },
  { n: "03", title: "Net peptide content", body: "The actual peptide mass per vial, not gross weight including salts and water." },
  { n: "04", title: "Endotoxin", body: "Screened to confirm bacterial endotoxin sits below a conservative threshold." },
  { n: "05", title: "Appearance", body: "Visual confirmation of the lyophilized cake or solution against spec." },
  { n: "06", title: "Heavy metals", body: "Tested to verify trace metals stay within safe analytical limits." },
  { n: "07", title: "Lot archival", body: "Every batch carries a unique lot number linked to its certificate and kept on file." },
];

const FAQS = [
  { q: "Are these products tested?", a: "Yes. Every lot is tested for identity and purity before it ships, and a certificate matched to your lot travels with the order. Purity is verified by HPLC and confirmed by mass spectrometry." },
  { q: "What does research use only mean?", a: "Every product is supplied strictly for laboratory and research use. They are not drugs, foods, supplements, or cosmetics, are not approved by the FDA, and are not for human or animal consumption." },
  { q: "How fast do orders ship?", a: "Orders placed on a business day are dispatched quickly from our US facility with tracking provided. Ground shipping is free on orders over $200." },
  { q: "How should compounds be stored?", a: "Store lyophilized material cool and dry, away from light. Once reconstituted, refrigerate. Always follow the handling notes on your certificate." },
  { q: "Do you provide a certificate of analysis?", a: "Yes. A certificate of analysis matched to your exact lot is printed and included with every order you receive." },
];

function SectionEyebrow({ children }) {
  return <p className="text-[10px] tracking-[0.32em] uppercase font-bold text-electric mb-4">{children}</p>;
}

function StatCounter({ value, suffix, label }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const dur = 1500;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="text-center">
      <div className="font-display font-bold text-4xl sm:text-5xl tracking-tighter text-white">
        {n}<span className="text-electric">{suffix}</span>
      </div>
      <div className="text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-white/45 mt-2">{label}</div>
    </div>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const [params] = useSearchParams();
  const query = (params.get("q") || "").toLowerCase();
  const catParam = params.get("cat") || "";

  useEffect(() => {
    api.get("/products").then((r) => {
      const all = r.data;
      setProducts(all.filter((p) => !p.is_bundle && p.category !== "Sea Moss"));
      setBundles(all.filter((p) => p.is_bundle));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!catParam) return;
    setActiveCat(catParam);
    const t = setTimeout(() => {
      const el = document.getElementById("catalog");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [catParam]);

  const featured = useMemo(() => products.find((p) => p.slug === "retatrutide-10mg") || products[0], [products]);
  const catList = useMemo(() => CAT_ORDER.filter((c) => products.some((p) => p.category === c)), [products]);
  const currentCat = catList.includes(activeCat) ? activeCat : (catList[0] || "");

  const searched = useMemo(() => {
    if (!query) return [];
    return products.filter((p) =>
      p.name.toLowerCase().includes(query) || (p.short_description || "").toLowerCase().includes(query)
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [products, query]);

  const tabItems = useMemo(
    () => products.filter((p) => p.category === currentCat).sort((a, b) => a.name.localeCompare(b.name)),
    [products, currentCat]
  );

  return (
    <div className="bg-[#070B1C]">
      {/* HERO, full-bleed athlete background */}
      <section className="relative overflow-hidden bg-[#070B1C] border-b border-white/10 min-h-[86vh] lg:min-h-screen flex items-center" data-testid="hero-visual">
        {HERO_VIDEO ? (
          <video autoPlay muted loop playsInline poster={HERO_POSTER} className="absolute inset-0 w-full h-full object-cover" data-testid="hero-video">
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
        ) : (
          <img src={ATHLETE_GIRL_IMG} alt="Research in motion" className="absolute inset-0 w-full h-full object-cover object-[72%_18%] lg:object-[78%_22%] hero-zoom" data-testid="hero-image" />
        )}
        {/* scrims */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(7,11,28,0.97) 0%, rgba(7,11,28,0.84) 42%, rgba(7,11,28,0.44) 72%, rgba(7,11,28,0.2) 100%)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 8% 42%, rgba(110,96,255,0.20), transparent 55%)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(7,11,28,0.55) 0%, transparent 24%, transparent 68%, rgba(7,11,28,0.9) 100%)" }} />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-16 pt-32 pb-24 lg:py-28">
          <div className="max-w-2xl flex flex-col items-start text-left">
            <p className="inline-flex items-center gap-2 sq-pill border border-white/12 bg-white/5 backdrop-blur-md px-4 py-2 text-[10px] sm:text-[11px] tracking-[0.26em] uppercase font-bold text-white/85 mb-8 fade-up" data-testid="hero-eyebrow">
              <ShieldCheck size={14} className="text-electric-300" /> Research grade · Documented to the lot
            </p>
            <h1 className="font-display font-medium text-6xl sm:text-7xl lg:text-[5.5rem] xl:text-[6.75rem] tracking-tighter uppercase leading-[0.85] text-white fade-up" style={{ animationDelay: "80ms" }}>
              Proof in <br className="hidden sm:block" />every <span className="text-electric-300">vial</span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-white/75 mt-8 max-w-xl leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
              Independent testing and a certificate of analysis matched to your exact lot, on every order.
              No cut powder. No mystery blends. Receipts over hype.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 fade-up" style={{ animationDelay: "240ms" }}>
              <a href="#catalog" className="btn-primary group inline-flex items-center justify-center gap-2 px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="hero-shop-primary">
                Shop Our Catalog <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
              </a>
              <Link to="/standards" className="btn-ghost inline-flex items-center justify-center px-9 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="hero-standards-cta">
                Our Standards
              </Link>
            </div>
            <p className="mt-6 text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold fade-up" style={{ animationDelay: "300ms" }}>For research use only · 21+</p>
          </div>
        </div>

        <a href="#catalog" className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/45 hover:text-electric transition-colors z-20" aria-label="Scroll to catalog">
          <span className="text-[9px] tracking-[0.3em] uppercase">Scroll</span>
          <ChevronDown size={16} className="animate-bounce" />
        </a>
      </section>

      {/* TRUST ICON BAR */}
      <section className="border-b border-white/10 bg-[#070B1C]" data-testid="trust-bar">
        <div className="max-w-[1300px] mx-auto grid grid-cols-2 md:grid-cols-5">
          {TIB.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={b.t} className={`flex items-center gap-3 px-5 py-6 justify-center ${i < 4 ? "md:border-r border-white/8" : ""} ${i < 4 ? "border-b md:border-b-0 border-white/8" : ""}`}>
                <Icon size={22} className="text-electric shrink-0" strokeWidth={1.7} />
                <div>
                  <div className="text-[11px] tracking-[0.1em] uppercase font-bold text-white leading-none">{b.t}</div>
                  <div className="text-[10px] text-white/45 mt-1">{b.s}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CATALOG, American Peptides style category tabs */}
      <section id="catalog" className="max-w-[1400px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <div className="mb-8">
          <SectionEyebrow>The research catalog</SectionEyebrow>
          <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white">Every compound, documented</h2>
          {!query && products.length > 0 && (
            <p className="text-base text-white/55 mt-4 max-w-2xl leading-relaxed" data-testid="catalog-count">
              {products.length} research compounds across {catList.length} categories. Every lot is
              tested and ships with a certificate matched to its number. Pick a category to browse.
            </p>
          )}
        </div>

        {query ? (
          <>
            <p className="text-xs tracking-[0.15em] uppercase text-white/40 mb-6">Results for "{query}" · {searched.length} found</p>
            {searched.length === 0 ? (
              <p className="py-16 text-center text-sm tracking-[0.2em] uppercase text-white/40">No products found</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="product-grid">
                {searched.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </>
        ) : loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="border border-white/12 aspect-[3/4] bg-[#0E1430] animate-pulse sq-lg" />)}
          </div>
        ) : (
          <>
            {/* Category tab bar */}
            <div className="border-b border-white/12 mb-9 -mx-5 px-5 lg:mx-0 lg:px-0 overflow-x-auto scrollbar-none" data-testid="catalog-tabs">
              <div className="flex gap-1 min-w-max">
                {catList.map((c) => {
                  const active = currentCat === c;
                  const count = products.filter((p) => p.category === c).length;
                  return (
                    <button
                      key={c}
                      onClick={() => setActiveCat(c)}
                      className={`relative flex items-center gap-2 px-5 py-4 text-[11px] tracking-[0.16em] uppercase font-bold whitespace-nowrap transition-colors duration-200 ${active ? "text-white" : "text-white/45 hover:text-white/80"}`}
                      data-testid={`catalog-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {c}
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 sq-xs ${active ? "bg-electric text-black" : "bg-white/10 text-white/50"}`}>{count}</span>
                      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-electric" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="product-grid" key={currentCat}>
              {tabItems.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </>
        )}
      </section>

      {/* FEATURED BUNDLES */}
      <section className="border-y border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <SectionEyebrow>Research protocols</SectionEyebrow>
              <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white">Curated research protocols</h2>
            </div>
            <Link to="/bundles" className="btn-ghost inline-flex items-center gap-2 px-7 py-3.5 text-xs tracking-[0.2em] uppercase font-bold self-start lg:self-auto" data-testid="view-all-bundles">
              View all protocols <ArrowRight size={14} />
            </Link>
          </div>
          {bundles.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="featured-bundles">
              {bundles.slice(0, 4).map((b, i) => <BundleCard key={b.id} product={b} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="border-b border-white/10 bg-[#0A0F26]" data-testid="stats-band">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => <StatCounter key={s.label} {...s} />)}
        </div>
      </section>

      {/* FEATURE, #1 research compound - full-bleed macro background */}
      {featured && (
        <section className="relative overflow-hidden border-b border-white/10" data-testid="feature-section">
          <div className="relative min-h-[520px] lg:min-h-[600px] flex items-center bg-[#0A0F26]">
            <img src={RETA_FEATURE_IMG} alt={featured.name} className="absolute inset-0 w-full h-full object-cover object-center" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,11,28,0.96) 0%, rgba(7,11,28,0.84) 40%, rgba(7,11,28,0.46) 70%, rgba(7,11,28,0.24) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 50%, rgba(110,96,255,0.20), transparent 55%)" }} />
            <div className="relative max-w-[1400px] mx-auto px-5 lg:px-16 w-full">
              <div className="max-w-xl">
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-electric mb-5">#1 Research compound</p>
                <h2 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-[0.88] text-white">{featured.name}</h2>
                <p className="text-base text-white/70 mt-6 max-w-lg leading-relaxed">{featured.description}</p>
                <p className="mt-6 font-display font-bold text-lg text-white">from <span className="text-electric">{money(featured.price)}</span></p>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link to={`/products/${featured.slug}`} className="btn-primary group inline-flex items-center justify-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="feature-shop-cta">
                    Shop {featured.name.split(" ")[0]} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
                  </Link>
                  <Link to="/?cat=Analog%20Peptides" className="btn-ghost inline-flex items-center justify-center px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="feature-browse-cta">
                    Browse Metabolic
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PROOF BAND */}
      <section className="relative border-b border-white/10">
        <div className="relative min-h-[460px] lg:min-h-[540px] flex items-center bg-[#0A0F26]">
          <img src={ATHLETE_IMG} alt="Research in motion" className="absolute inset-0 w-full h-full object-cover object-[44%_18%] lg:object-[72%_22%]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,11,28,0.94) 0%, rgba(7,11,28,0.74) 44%, rgba(7,11,28,0.3) 100%)" }} />
          <div className="relative max-w-[1400px] mx-auto px-5 lg:px-16 w-full">
            <div className="max-w-lg">
              <SectionEyebrow>Documented, not claimed</SectionEyebrow>
              <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white">A standard you can verify</h2>
              <p className="text-base text-white/70 mt-6 leading-relaxed max-w-md">
                We publish the proof, not the promise. Every lot is characterized before release and the
                analytical results travel with the vial you receive.
              </p>
              <Link to="/standards" className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="proof-standards-cta">
                How we test <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BEYOND PURITY */}
      <section className="max-w-[1200px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <SectionEyebrow>Beyond purity</SectionEyebrow>
        <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tighter uppercase text-white max-w-3xl leading-[0.98]">What 99% pure does not tell you</h2>
        <p className="text-base text-white/60 mt-6 max-w-2xl leading-relaxed">
          Purity testing measures one thing. HPLC and mass spectrometry were built to measure the molecule,
          not what surrounds it. We test for the four things that number cannot see.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-12" data-testid="beyond-purity">
          {BEYOND.map((b, i) => (
            <div key={b.n} className="border border-white/12 bg-[#0B1024] p-7 lg:p-8 sq-lg fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`beyond-${b.n}`}>
              <div className="font-display font-bold text-4xl text-white/15">{b.n}</div>
              <h3 className="font-display font-bold text-xl uppercase tracking-tight text-white mt-3 mb-4">{b.title}</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-[9px] tracking-[0.22em] uppercase text-white/35 mb-1">Why purity misses it</div>
                  <p className="text-sm text-white/55 leading-relaxed">{b.misses}</p>
                </div>
                <div>
                  <div className="text-[9px] tracking-[0.22em] uppercase text-electric/70 mb-1">Why it matters</div>
                  <p className="text-sm text-white/75 leading-relaxed">{b.matters}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="border-y border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <SectionEyebrow>Shop by research area</SectionEyebrow>
          <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-12">Find your compound</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pathways">
            {PATHWAYS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Link key={p.n} to={p.to} className="group border border-white/12 bg-[#0B1024] p-7 sq-lg fade-up hover:-translate-y-1 hover:border-electric/40 transition-all duration-300" style={{ animationDelay: `${i * 50}ms` }} data-testid={`pathway-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-11 h-11 flex items-center justify-center bg-electric/10 border border-electric/30 sq-md">
                      <Icon size={19} className="text-electric" />
                    </div>
                    <span className="font-display font-bold text-2xl text-white/12">{p.n}</span>
                  </div>
                  <h3 className="font-display font-bold text-lg uppercase tracking-tight text-white">{p.title}</h3>
                  <p className="text-sm text-white/55 mt-2 leading-relaxed">{p.body}</p>
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-electric mt-5 group-hover:gap-2.5 transition-all duration-200">Explore <ArrowRight size={13} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* MADE IN USA */}
      <section className="relative border-b border-white/10">
        <div className="relative min-h-[440px] lg:min-h-[520px] flex items-center bg-[#0A0F26]">
          <img src={MOTSC_IMG} alt="MOTS-c research vial" className="absolute inset-0 w-full h-full object-cover object-[72%_center] lg:object-[78%_center]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,11,28,0.94) 0%, rgba(7,11,28,0.72) 44%, rgba(7,11,28,0.3) 100%)" }} />
          <div className="relative max-w-[1400px] mx-auto px-5 lg:px-16 w-full">
            <div className="max-w-lg">
              <SectionEyebrow>Built on data</SectionEyebrow>
              <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-tighter uppercase leading-none text-white">Tested and shipped in the USA</h2>
              <p className="text-base text-white/70 mt-6 leading-relaxed max-w-md">
                Century Labs runs on documented quality control. Tested before release and fulfilled from
                our own facility. When you order, you get the paperwork to match.
              </p>
              <a href="#catalog" className="btn-primary inline-flex items-center gap-2 mt-8 px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold" data-testid="usa-shop-cta">
                Shop the catalog <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="max-w-[1200px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <SectionEyebrow>Why researchers choose us</SectionEyebrow>
        <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-12">The standard in research grade peptides</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="commitments">
          {COMMITMENTS.map((c, i) => (
            <div key={c.n} className="border border-white/12 bg-[#0B1024] p-7 sq-lg fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/35 mb-3">{c.n}</div>
              <div className="font-display font-bold text-2xl tracking-tighter text-electric">{c.stat}</div>
              <h3 className="font-display font-bold text-base uppercase tracking-tight text-white mt-4 mb-2">{c.title}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VERIFIED BADGES */}
      <section className="border-y border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-12">
          <p className="text-center text-[10px] tracking-[0.28em] uppercase text-white/40 mb-8">Verified across every batch</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {VERIFIED.map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.label} className="flex flex-col items-center text-center gap-3 border border-white/10 bg-[#0B1024] px-4 py-6 sq-md">
                  <Icon size={22} className="text-electric" />
                  <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-white/70 leading-tight">{v.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTING STANDARD */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <SectionEyebrow>The testing standard</SectionEyebrow>
        <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white max-w-3xl leading-[0.98]">Seven checks before a batch is offered</h2>
        <p className="text-base text-white/60 mt-6 max-w-2xl leading-relaxed">
          Each lot is assayed by an independent laboratory before it is listed, and the signed certificate
          is matched to the exact vial you receive.
        </p>
        <div className="mt-12 border border-white/10 sq-lg overflow-hidden" data-testid="testing-standard">
          {TESTING.map((t, i) => (
            <div key={t.n} className={`flex items-start gap-5 p-6 lg:px-8 ${i % 2 ? "bg-[#0A0F26]" : "bg-[#0B1024]"} ${i ? "border-t border-white/8" : ""}`}>
              <span className="font-display font-bold text-2xl text-electric/40 shrink-0 w-10">{t.n}</span>
              <div>
                <h3 className="font-display font-bold text-base uppercase tracking-tight text-white">{t.title}</h3>
                <p className="text-sm text-white/55 mt-1.5 leading-relaxed">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VERIFY YOUR LOT */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="border border-electric/25 bg-[#0B1024] p-8 lg:p-12 sq-xl" data-testid="verify-section">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={18} className="text-electric" />
                <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-electric">Documented to the lot</span>
              </div>
              <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white leading-none">Every order ships with a COA</h2>
              <p className="text-base text-white/65 mt-5 leading-relaxed">
                No lookup, no guessing. A lot matched certificate of analysis is printed and packed with
                every order, matched to the exact vial you receive.
              </p>
            </div>
            <div>
              <div className="border border-electric/30 bg-[#0A0F26] p-6 sq-lg" data-testid="coa-included-card">
                <div className="flex items-center gap-2 mb-3">
                  <ScrollText size={18} className="text-electric" />
                  <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-electric">Included with every order</span>
                </div>
                <p className="text-sm text-white/65 leading-relaxed">
                  A lot matched certificate of analysis is printed and packed in the box, documenting the
                  independent purity and identity results for the exact vial you receive.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STANDARDS SUMMARY */}
      <section className="border-y border-white/10 bg-[#0A0F26]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid sm:grid-cols-3 gap-8">
          {[
            { icon: FlaskConical, t: "Analytical purity", b: "Verified by HPLC and confirmed by mass spectrometry. Each lot is characterized before release." },
            { icon: ScrollText, t: "COA with every order", b: "A certificate matched to your lot documents identity, purity, and results, included with the order." },
            { icon: PackageCheck, t: "US fulfillment", b: "Tested inside the United States, then packed and shipped from our own facility with tracking." },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.t}>
                <Icon size={24} className="text-electric mb-4" />
                <h3 className="font-display font-bold text-base uppercase tracking-tight text-white mb-2">{s.t}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{s.b}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <SectionEyebrow>Answers</SectionEyebrow>
        <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tighter uppercase text-white mb-10">Frequently asked questions</h2>
        <div data-testid="home-faq">
          {FAQS.map((f, i) => (
            <div key={i} className="border-b border-white/10">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-6 text-left text-white" data-testid={`faq-toggle-${i}`}>
                <span className="font-display font-bold text-base sm:text-lg tracking-tight">{f.q}</span>
                <ChevronDown size={18} className={`shrink-0 text-electric transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="pb-6 text-sm text-white/60 leading-relaxed max-w-2xl">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
